// biome-ignore-all lint/performance/noImgElement: We are not using next/image here

import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations } from "@/hooks/use-translations";
import { defaultAnimationTransition } from "@/lib/animations";
import { cn } from "@/lib/utils";

type Testimonial = {
  message: string;
  author: string;
  title: string;
  avatar: string | null;
};

const SLIDE_DURATION_MS = 9000;
const SLIDE_DOT_WIDTH = 16;
const SLIDE_DOT_WIDTH_INACTIVE = 4;

export default function TestimonialSlideshow() {
  const { __, __raw } = useTranslations();
  const [currentIndex, setCurrentIndex] = useState(0);

  const avatars = [
    "/img/auth/tobias.avif",
    "/img/auth/ellen.avif",
    "/img/auth/dolores.avif",
    "/img/auth/stine.avif",
  ];

  const testimonials = useMemo<Testimonial[]>(
    () =>
      (__raw("auth.testimonials.items") as Testimonial[]).map((item) => ({
        ...item,
        avatar: avatars[currentIndex],
      })),
    [__raw, currentIndex]
  );

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, SLIDE_DURATION_MS);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const currentTestimonial = testimonials[currentIndex];

  return (
    <div className="absolute inset-x-10 bottom-10 flex flex-col gap-6">
      <AnimatePresence mode="wait">
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col gap-4"
          exit={{ opacity: 0, x: 2.5 }}
          initial={{ opacity: 0, y: 2.5 }}
          key={currentIndex}
          transition={defaultAnimationTransition}
        >
          <blockquote className="max-w-120 font-semibold text-2xl text-white leading-tight">
            &ldquo;{currentTestimonial.message}&rdquo;
          </blockquote>

          <div className="flex items-center gap-3">
            {currentTestimonial.avatar && (
              <img
                alt={currentTestimonial.author}
                className="h-9 w-9 rounded-full object-cover"
                height={36}
                src={currentTestimonial.avatar}
                width={36}
              />
            )}
            <div className="flex flex-col">
              <span className="font-semibold text-sm text-white">
                {currentTestimonial.author}
              </span>
              <span className="font-medium text-white/70 text-xs">
                {currentTestimonial.title}
              </span>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="flex gap-1">
        {testimonials.map((testimonial, index) => (
          <button
            aria-label={__("auth.testimonials.go_to_testimonial", {
              number: index + 1,
            })}
            className="relative h-1 cursor-pointer rounded-full bg-white/30 transition-colors hover:bg-white/50"
            key={testimonial.author}
            onClick={() => goToSlide(index)}
            type="button"
          >
            <motion.span
              animate={{
                width:
                  index === currentIndex
                    ? SLIDE_DOT_WIDTH
                    : SLIDE_DOT_WIDTH_INACTIVE,
              }}
              className={cn(
                "block h-full rounded-full",
                index === currentIndex ? "bg-white" : "bg-white/30"
              )}
              initial={false}
              transition={defaultAnimationTransition}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
